import { buildInsightsDisplayMetric } from "./insightsWeekOverview";
import { formatInsightsMetricDisplay } from "./insightsSetRowMath";

/** Function to compute average sets per trained muscle from rows.
 * @param {ReadonlyArray<{ primary?: number, secondary?: number }>} muscles
 */
function computeAvgSetsPerTrainedMuscleFromRows(muscles) {
  let sum = 0;
  let trainedCount = 0;
  for (const row of muscles) {
    const p = Number(row?.primary);
    const s = Number(row?.secondary);
    const primary = Number.isFinite(p) ? Math.max(0, p) : 0;
    const secondary = Number.isFinite(s) ? Math.max(0, s) : 0;
    const t = primary + secondary;
    if (t > 0) {
      sum += t;
      trainedCount += 1;
    }
  }
  return { avg: trainedCount > 0 ? sum / trainedCount : 0, trainedCount };
}

/** Function to format a set count for an alert.
 * @param {number} total
 * @returns {string}
 */
function formatSetCountForAlert(total) {
  if (!Number.isFinite(total)) {
    return "0";
  }
  if (Number.isInteger(total)) {
    return String(total);
  }
  return total.toFixed(1);
}

/** Function to get the total sets for a muscle row.
 * @param {object} row
 * @returns {number}
 */
function muscleRowTotalSets(row) {
  const p = Number(row?.primary);
  const s = Number(row?.secondary);
  const primary = Number.isFinite(p) ? Math.max(0, p) : 0;
  const secondary = Number.isFinite(s) ? Math.max(0, s) : 0;
  return primary + secondary;
}

const VOLUME_COVERAGE_MEV_MIN_SETS = 6;
const VOLUME_COVERAGE_MRV_MIN_SETS = 20;
const VOLUME_COVERAGE_GOOD_TRAINED_MIN = 7;

/** Function to build insights volume coverage alerts.
 * @param {ReadonlyArray<{ id: string, primary: number, secondary: number }>} muscles
 */
export function buildInsightsVolumeCoverageAlerts(muscles) {
  const notTrained = [];
  const mrv = [];
  const mev = [];
  for (const row of muscles) {
    const total = muscleRowTotalSets(row);
    const { id } = row;
    if (total === 0) {
      notTrained.push(id);
    } else if (total >= VOLUME_COVERAGE_MRV_MIN_SETS) {
      mrv.push({ id, total });
    } else if (total < VOLUME_COVERAGE_MEV_MIN_SETS) {
      mev.push({ id, total });
    }
  }
  notTrained.sort((a, b) => a.localeCompare(b));
  mrv.sort((a, b) => a.id.localeCompare(b.id));
  mev.sort((a, b) => a.id.localeCompare(b.id));

  /** @type {{ variant: "info"|"warning"|"success", messageKey: string, params?: object }[]} */
  const alerts = [];
  for (const groupKey of notTrained) {
    alerts.push({
      variant: "warning",
      messageKey: "alertNotTrained",
      params: { groupKey },
    });
  }
  for (const { id, total } of mrv) {
    alerts.push({
      variant: "warning",
      messageKey: "alertAboveMrv",
      params: { groupKey: id, sets: formatSetCountForAlert(total) },
    });
  }
  for (const { id, total } of mev) {
    alerts.push({
      variant: "info",
      messageKey: "alertBelowMev",
      params: { groupKey: id, sets: formatSetCountForAlert(total) },
    });
  }

  const trainedCount = muscles.filter((row) => muscleRowTotalSets(row) > 0)
    .length;
  if (
    trainedCount >= VOLUME_COVERAGE_GOOD_TRAINED_MIN &&
    notTrained.length === 0 &&
    mrv.length === 0
  ) {
    alerts.push({
      variant: "success",
      messageKey: "alertGoodCoverage",
      params: { count: trainedCount },
    });
  }
  return alerts;
}

/** Function to build insights volume coverage metrics.
 *
 * `amrapSets` comes straight from the week scan rather than from a chart
 * bucket: prescribed AMRAP sets are charted on the RPE/RiR histogram, so the
 * Rep Range buckets are no longer a complete source for this count.
 *
 * @param {ReadonlyArray<{ id: string, primary: number, secondary: number }>} muscles
 * @param {number} amrapSets
 * @param {number} sessionCount
 * @param {{ sessionCount: number, muscles: typeof muscles, amrapSets: number } | null} prev
 */
export function buildInsightsVolumeCoverageMetrics(
  muscles,
  amrapSets,
  sessionCount,
  prev
) {
  const amrapCount = amrapSets ?? 0;
  const { avg: avgSets } = computeAvgSetsPerTrainedMuscleFromRows(muscles);

  let prevSessionCount = null;
  let prevAvgSets = null;
  let prevAmrap = null;
  if (prev) {
    prevSessionCount = prev.sessionCount;
    prevAvgSets = computeAvgSetsPerTrainedMuscleFromRows(prev.muscles).avg;
    prevAmrap = prev.amrapSets ?? 0;
  }

  return [
    buildInsightsDisplayMetric({
      id: "workoutsPerWeek",
      cur: sessionCount,
      prev: prevSessionCount,
      formatDisplay: formatInsightsMetricDisplay,
    }),
    buildInsightsDisplayMetric({
      id: "avgSetsPerMuscle",
      cur: avgSets,
      prev: prevAvgSets,
      formatDisplay: formatInsightsMetricDisplay,
    }),
    buildInsightsDisplayMetric({
      id: "amrapSets",
      cur: amrapCount,
      prev: prevAmrap,
      formatDisplay: formatInsightsMetricDisplay,
    }),
  ];
}
